/**
 * Telephony Accounts page (TELEPHONY.md §"Telephony Accounts"). One
 * row per legal entity (EIN). Each Telephony Account wraps a Twilio
 * subaccount that owns its own phone numbers, messaging services, and
 * brand registration.
 *
 * Slice 1 surfaces only Telephony Account list + create + delete.
 * Phone-number provisioning, Trust Hub, brand registration, and
 * campaign wizard ship in later slices and will mount under this
 * same page (one Telephony Account drill-in per row).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Link2, Plus, Trash2, Unlink } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@lumika/ui";
import {
  COUNTRIES,
  CreateTelephonyAccountSchema,
  TelephonyBusinessTypeSchema,
  US_STATES,
  type CreateTelephonyAccountDto,
  type TelephonyAccount,
  type TelephonyAccountSearchResult,
  type TelephonyBusinessType,
} from "@lumika/shared";
import { adminApi } from "../../api/admin";
import { formatDateTime } from "../../lib/format";

export const BRAND_VARIANT: Record<
  TelephonyAccount["brandStatus"],
  "default" | "success" | "warning" | "secondary" | "destructive"
> = {
  NOT_STARTED: "secondary",
  DRAFT: "secondary",
  PENDING_OTP: "warning",
  IN_REVIEW: "warning",
  APPROVED: "success",
  FAILED: "destructive",
};

export function DealershipTelephonyPage() {
  const { dealershipId } = useParams();
  if (!dealershipId) return null;

  const accountsQ = useQuery({
    queryKey: ["admin", dealershipId, "telephony-accounts"],
    queryFn: () => adminApi.telephonyAccounts(dealershipId),
  });

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <PageHeader
        eyebrow="Telephony"
        title="Telephony Accounts"
        description="Each Telephony Account corresponds to one legal business entity (EIN). Phone numbers and SMS brand registrations live under their own account."
      />
      <AccountsSection
        dealershipId={dealershipId}
        accounts={accountsQ.data ?? []}
        isLoading={accountsQ.isLoading}
        isError={accountsQ.isError}
      />
    </div>
  );
}

function AccountsSection({
  dealershipId,
  accounts,
  isLoading,
  isError,
}: {
  dealershipId: string;
  accounts: TelephonyAccount[];
  isLoading: boolean;
  isError: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  return (
    <Section
      title="Accounts"
      description="Create one Telephony Account per legal business entity (EIN). If your dealership group operates multiple brands under separate EINs, create a Telephony Account for each. An account can serve several dealerships — connect an existing one instead of duplicating it."
      actions={
        <>
          <Button variant="outline" onClick={() => setConnectOpen(true)}>
            <Link2 className="h-4 w-4" /> Connect Existing
          </Button>
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> New Telephony Account
          </Button>
        </>
      }
    >
      {isLoading ? (
        <PageLoading message="Loading Telephony Accounts…" />
      ) : isError ? (
        <PageError
          title="Couldn't load Telephony Accounts"
          description="Try refreshing the page."
        />
      ) : accounts.length === 0 ? (
        <EmptyState
          title="No Telephony Accounts yet"
          description="Create one to start provisioning phone numbers and registering this dealership's brand for SMS."
        />
      ) : (
        <AccountsTable dealershipId={dealershipId} rows={accounts} />
      )}
      {open && (
        <CreateAccountDialog
          dealershipId={dealershipId}
          onClose={() => setOpen(false)}
        />
      )}
      {connectOpen && (
        <ConnectExistingDialog
          dealershipId={dealershipId}
          onClose={() => setConnectOpen(false)}
        />
      )}
    </Section>
  );
}

/**
 * "Connect Existing" — search Telephony Accounts across the whole
 * platform (excluding ones already connected here) and link one to
 * this dealership. Linking only creates the account↔dealership
 * association; the Twilio subaccount is untouched.
 */
function ConnectExistingDialog({
  dealershipId,
  onClose,
}: {
  dealershipId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  // Debounce the free-text search 300ms so each keystroke doesn't fire
  // a request; the debounced value drives the server query.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  const trimmed = debouncedSearch.trim();

  const resultsQ = useQuery({
    queryKey: ["admin", "telephony-account-search", dealershipId, trimmed],
    queryFn: () => adminApi.searchTelephonyAccounts(trimmed, dealershipId),
    enabled: trimmed.length > 0,
  });
  const connect = useMutation({
    mutationFn: (accountId: string) =>
      adminApi.connectTelephonyAccount(dealershipId, accountId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", dealershipId, "telephony-accounts"] });
      toast.success("Telephony Account connected");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const rows: TelephonyAccountSearchResult[] = resultsQ.data ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Connect Existing Telephony Account</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <p className="rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground">
            Connecting shares the account (its legal entity, brand registration, and
            number inventory container) with this dealership. Numbers purchased here
            stay attributed to this dealership.
          </p>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by display name, legal name, or account SID…"
            autoFocus
          />
          {trimmed.length === 0 ? (
            <EmptyState
              title="Search to find accounts"
              description="Accounts already connected to this dealership are excluded."
            />
          ) : resultsQ.isLoading ? (
            <PageLoading message="Searching…" />
          ) : resultsQ.isError ? (
            <PageError title="Search failed" description="Try again." />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No matching accounts"
              description="Nothing connectable matches that search."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Connected to</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="font-medium">{row.displayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.legalBusinessName ?? row.twilioAccountSid}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.connectedDealerships.length === 0 ? (
                        <span className="text-muted-foreground/60">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {row.connectedDealerships.map((d) => (
                            <Badge key={d.id} variant="secondary">
                              {d.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        onClick={() => connect.mutate(row.id)}
                        disabled={connect.isPending}
                      >
                        <Link2 className="h-4 w-4" /> Connect
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountsTable({
  dealershipId,
  rows,
}: {
  dealershipId: string;
  rows: TelephonyAccount[];
}) {
  return (
    <>
      <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Display name</TableHead>
              <TableHead>Legal entity</TableHead>
              <TableHead>EIN</TableHead>
              <TableHead>Brand status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <AccountRow key={row.id} dealershipId={dealershipId} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableBleed>
      <MobileList>
        {rows.map((row) => (
          <AccountMobileRow key={row.id} dealershipId={dealershipId} row={row} />
        ))}
      </MobileList>
    </>
  );
}

function useAccountRowActions(dealershipId: string, row: TelephonyAccount) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", dealershipId, "telephony-accounts"] });
  const remove = useMutation({
    mutationFn: () => adminApi.removeTelephonyAccount(row.id),
    onSuccess: () => {
      invalidate();
      toast.success("Telephony Account deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const disconnect = useMutation({
    mutationFn: () => adminApi.disconnectTelephonyAccount(dealershipId, row.id),
    onSuccess: () => {
      invalidate();
      toast.success("Telephony Account disconnected");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const onDisconnect = () => {
    const ok = window.confirm(
      `Disconnect Telephony Account "${row.displayName}" from this dealership?\n\n` +
        `The account itself stays intact (other dealerships keep using it); only this ` +
        `dealership's connection is removed. Blocked while this connection still has ` +
        `active phone numbers.`,
    );
    if (ok) disconnect.mutate();
  };
  const onDelete = () => {
    const ok = window.confirm(
      `Delete Telephony Account "${row.displayName}"?\n\n` +
        `This closes the underlying account at the carrier and destroys every messaging ` +
        `service and brand registration owned by it. Only possible when the account has ` +
        `no active phone numbers and no other connected dealerships. This cannot be undone.`,
    );
    if (ok) remove.mutate();
  };

  return { remove, disconnect, onDisconnect, onDelete };
}

function AccountRow({
  dealershipId,
  row,
}: {
  dealershipId: string;
  row: TelephonyAccount;
}) {
  const { remove, disconnect, onDisconnect, onDelete } = useAccountRowActions(
    dealershipId,
    row,
  );

  return (
    <TableRow>
      <TableCell className="font-medium">
        <Link
          to={`/dealerships/${dealershipId}/telephony/${row.id}`}
          className="inline-flex items-center gap-1 hover:underline"
        >
          {row.displayName}
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
        </Link>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {row.legalBusinessName ?? <span className="text-muted-foreground/60">—</span>}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {row.einLast4 ? `••-•••${row.einLast4}` : <span className="text-muted-foreground/60">—</span>}
      </TableCell>
      <TableCell>
        <Badge variant={BRAND_VARIANT[row.brandStatus]}>{row.brandStatus}</Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">{formatDateTime(row.createdAt)}</TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={onDisconnect}
          disabled={disconnect.isPending}
          aria-label="Disconnect Telephony Account from this dealership"
        >
          <Unlink className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={remove.isPending}
          aria-label="Delete Telephony Account"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function AccountMobileRow({
  dealershipId,
  row,
}: {
  dealershipId: string;
  row: TelephonyAccount;
}) {
  const { remove, disconnect, onDisconnect, onDelete } = useAccountRowActions(
    dealershipId,
    row,
  );

  return (
    <MobileListRow
      primary={
        <Link
          to={`/dealerships/${dealershipId}/telephony/${row.id}`}
          className="inline-flex items-center gap-1 hover:underline"
        >
          {row.displayName}
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
        </Link>
      }
      secondary={
        row.legalBusinessName ??
        (row.einLast4 ? `••-•••${row.einLast4}` : undefined)
      }
      tertiary={
        row.legalBusinessName && row.einLast4 ? `EIN ••-•••${row.einLast4}` : undefined
      }
      meta={
        <>
          <Badge variant={BRAND_VARIANT[row.brandStatus]}>{row.brandStatus}</Badge>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {formatDateTime(row.createdAt)}
          </span>
        </>
      }
      trailing={
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDisconnect}
            disabled={disconnect.isPending}
            aria-label="Disconnect Telephony Account from this dealership"
          >
            <Unlink className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            disabled={remove.isPending}
            aria-label="Delete Telephony Account"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      }
    />
  );
}

type FormShape = {
  displayName: string;
  legalBusinessName: string;
  ein: string;
  businessAddressLine1: string;
  businessAddressLine2: string;
  businessCity: string;
  businessState: string;
  businessPostalCode: string;
  businessCountry: string;
  websiteUrl: string;
  businessType: TelephonyBusinessType | "";
};

// Labels for the shared TelephonyBusinessTypeSchema enum. Exported so
// the A2P wizard's business-type select shows the same wording — the
// vocabulary itself comes from the schema, never a local copy.
export const BUSINESS_TYPE_LABELS: Record<TelephonyBusinessType, string> = {
  LLC: "LLC",
  CORPORATION: "Corporation",
  PARTNERSHIP: "Partnership",
  NON_PROFIT: "Non-profit",
  SOLE_PROPRIETOR: "Sole proprietor",
};

const blankForm: FormShape = {
  displayName: "",
  legalBusinessName: "",
  ein: "",
  businessAddressLine1: "",
  businessAddressLine2: "",
  businessCity: "",
  businessState: "",
  businessPostalCode: "",
  businessCountry: "US",
  websiteUrl: "",
  businessType: "",
};

function CreateAccountDialog({
  dealershipId,
  onClose,
}: {
  dealershipId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // `useZodForm`'s value-type is locked to the schema's input shape,
  // which makes optionals `T | undefined` — a poor fit for `<Input
  // value={…}>` (which needs `string`). Plain `useState<FormShape>`
  // keeps the field types narrow; server-side validation runs on
  // POST regardless.
  const [values, setValues] = useState<FormShape>(blankForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormShape, string>>>({});
  const setField = <K extends keyof FormShape>(key: K, v: FormShape[K]) => {
    setValues((cur) => ({ ...cur, [key]: v }));
    // Clear a field's error as soon as the operator starts fixing it.
    setErrors((cur) => (cur[key] ? { ...cur, [key]: undefined } : cur));
  };
  const create = useMutation({
    mutationFn: (dto: CreateTelephonyAccountDto) =>
      adminApi.createTelephonyAccount(dealershipId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", dealershipId, "telephony-accounts"] });
      toast.success("Telephony Account created");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  function submit() {
    // Validate against the SAME schema the backend enforces so a bad EIN /
    // ZIP / state / URL is highlighted on the offending field BEFORE the
    // POST — instead of bouncing back as an opaque server-side toast.
    const result = CreateTelephonyAccountSchema.safeParse({
      displayName: values.displayName.trim(),
      legalBusinessName: emptyToUndef(values.legalBusinessName),
      ein: emptyToUndef(values.ein),
      businessAddressLine1: emptyToUndef(values.businessAddressLine1),
      businessAddressLine2: emptyToUndef(values.businessAddressLine2),
      businessCity: emptyToUndef(values.businessCity),
      businessState: emptyToUndef(values.businessState),
      businessPostalCode: emptyToUndef(values.businessPostalCode),
      businessCountry: values.businessCountry || "US",
      websiteUrl: emptyToUndef(values.websiteUrl),
      businessType: values.businessType === "" ? undefined : values.businessType,
    });
    if (!result.success) {
      const next: Partial<Record<keyof FormShape, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof FormShape | undefined;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    create.mutate(result.data);
  }

  // Block backdrop-click / Escape dismissal once the operator has typed
  // anything — closing unmounts the dialog and loses the entry.
  const isDirty = Object.keys(blankForm).some(
    (k) => values[k as keyof FormShape] !== blankForm[k as keyof FormShape],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>New Telephony Account</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <p className="rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground">
            Create one Telephony Account per legal business entity (EIN). If your dealership
            group operates multiple brands under separate EINs, create a Telephony Account
            for each. Phone numbers and brand registrations live under their own account.
          </p>
          <FormField label="Display name" required error={errors.displayName}>
            <Input
              value={values.displayName}
              onChange={(e) => setField("displayName", e.target.value)}
              placeholder="e.g. Bayside Auto Group — Primary"
              autoFocus
            />
          </FormField>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Legal business name" error={errors.legalBusinessName}>
              <Input
                value={values.legalBusinessName}
                onChange={(e) => setField("legalBusinessName", e.target.value)}
                placeholder="As registered with the IRS"
              />
            </FormField>
            <FormField label="EIN" error={errors.ein}>
              <Input
                value={values.ein}
                onChange={(e) => setField("ein", e.target.value)}
                placeholder="12-3456789"
                inputMode="numeric"
              />
            </FormField>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Business type" error={errors.businessType}>
              <Select
                value={values.businessType === "" ? undefined : values.businessType}
                onValueChange={(v) => setField("businessType", v as TelephonyBusinessType)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {TelephonyBusinessTypeSchema.options.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {BUSINESS_TYPE_LABELS[opt]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Website" error={errors.websiteUrl}>
              <Input
                value={values.websiteUrl}
                onChange={(e) => setField("websiteUrl", e.target.value)}
                placeholder="https://"
                inputMode="url"
              />
            </FormField>
          </div>
          <FormField label="Address line 1" error={errors.businessAddressLine1}>
            <Input
              value={values.businessAddressLine1}
              onChange={(e) => setField("businessAddressLine1", e.target.value)}
              placeholder="Street address"
            />
          </FormField>
          <FormField label="Address line 2" error={errors.businessAddressLine2}>
            <Input
              value={values.businessAddressLine2}
              onChange={(e) => setField("businessAddressLine2", e.target.value)}
              placeholder="Suite, unit, etc."
            />
          </FormField>
          <div className="grid gap-4 md:grid-cols-4">
            <FormField label="City" error={errors.businessCity}>
              <Input
                value={values.businessCity}
                onChange={(e) => setField("businessCity", e.target.value)}
              />
            </FormField>
            <FormField label="State" error={errors.businessState}>
              <Select
                value={values.businessState === "" ? "__none__" : values.businessState}
                onValueChange={(v) => setField("businessState", v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a state…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {US_STATES.map((s) => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.code} — {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Postal code" error={errors.businessPostalCode}>
              <Input
                value={values.businessPostalCode}
                onChange={(e) => setField("businessPostalCode", e.target.value)}
              />
            </FormField>
            <FormField label="Country" error={errors.businessCountry}>
              <Select
                value={values.businessCountry}
                onValueChange={(v) => setField("businessCountry", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function emptyToUndef(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}
